"use client";

import { Check, Fingerprint, Loader2, Lock, Mail, UserPlus } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Separator } from "@/components/ui/separator";
import { getAvailableProviders } from "@/lib/actions/auth-providers";
import { getLoginProbe, requestEmailVerificationByEmail } from "@/lib/actions/users";
import { authClient } from "@/lib/auth-client";

/* -- Icons -- */
const ProviderIcon = ({ id }: { id: string }) => {
    if (id === "google") {
        return (
            <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
        );
    }
    if (id === "microsoft") {
        return (
            <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden="true">
                <path fill="#F35325" d="M1 1h10v10H1z" />
                <path fill="#81BC06" d="M12 1h10v10H12z" />
                <path fill="#05A6F0" d="M1 12h10v10H1z" />
                <path fill="#FFBA08" d="M12 12h10v10H12z" />
            </svg>
        );
    }
    return null;
};

const providerOrder = ["google", "microsoft"];
const providerLabels: Record<string, string> = {
    microsoft: "Mit Microsoft",
    google: "Mit Google anmelden",
};

export function LoginForm({ hasPasskeys = false }: { hasPasskeys?: boolean }) {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [verificationHint, setVerificationHint] = useState<string | null>(null);
    const [resendStatus, setResendStatus] = useState<string | null>(null);
    const [resendLoading, setResendLoading] = useState(false);
    const [emailValue, setEmailValue] = useState("");
    const [providers, setProviders] = useState<Record<string, { id: string; name: string }> | null>(null);
    const [mounted, setMounted] = useState(false);
    const [passkeySupported, setPasskeySupported] = useState(false);
    const [showRegisterHint, setShowRegisterHint] = useState(false);

    const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutId = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
        });
        try {
            return await Promise.race([promise, timeoutPromise]);
        } finally {
            if (timeoutId) clearTimeout(timeoutId);
        }
    };

    useEffect(() => {
        setMounted(true);
        if (typeof window !== 'undefined') {
            if (window.PublicKeyCredential) setPasskeySupported(true);
        }
    }, []);
    const setLoginRedirectCookie = () => {
        if (typeof document === "undefined") return;
        const isSecure = window.location.protocol === "https:";
        document.cookie = `fromLogin=1; Path=/; Max-Age=300; SameSite=Lax${isSecure ? "; Secure" : ""}`;
    };

    useEffect(() => {
        let cancelled = false;

        getAvailableProviders().then((res) => {
            if (!cancelled) {
                const map: Record<string, { id: string; name: string }> = {};
                for (const p of res) {
                    map[p.id] = p;
                }
                setProviders(map);
            }
        });

        return () => {
            cancelled = true;
        };
    }, []);

    const orderedProviders = useMemo(() => {
        if (!providers) return [];
        const social = Object.values(providers).filter((p) => p.id !== "credentials");
        return social.sort((a, b) => {
            const indexA = providerOrder.indexOf(a.id);
            const indexB = providerOrder.indexOf(b.id);
            return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
        });
    }, [providers]);

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setIsLoading("credentials");
        setError(null);
        setShowRegisterHint(false);
        setVerificationHint(null);
        setResendStatus(null);

        const formData = new FormData(event.currentTarget);
        const email = formData.get("email") as string;
        const password = formData.get("password") as string;

        try {
            const callbackUrl = "/dashboard?fromLogin=1";
            setLoginRedirectCookie();
            const result = await withTimeout(
                authClient.signIn.email({
                    email,
                    password,
                }),
                12_000,
                "signIn"
            );

            if (result.error) {
                // Code-review L-A (2026-05-13): single combined probe
                // replaces the previous getEmailVerificationStatus +
                // checkUserSuspended pair (each ran bcrypt and burned
                // its own rate-limit tokens — failed logins from shared
                // NATs tripped the limiter prematurely).
                const probe = await getLoginProbe(email, password);
                const data = probe.success ? probe.data : null;
                if (data && data.status === 'unverified') {
                    setError("E-Mail-Adresse ist noch nicht bestätigt");
                    if (data.tokenExpired) {
                        setVerificationHint("Der Bestätigungslink ist abgelaufen. Bitte neue E-Mail anfordern.");
                    } else {
                        setVerificationHint("Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link in Ihrem Postfach.");
                    }
                } else if (data && data.status === 'suspended') {
                    setError("Ihr Konto wurde deaktiviert. Bitte kontaktieren Sie den Administrator.");
                } else {
                    setError("Ungültige E-Mail-Adresse oder Passwort");
                    setShowRegisterHint(true);
                }
                setIsLoading(null);
            } else {
                // Sign-in succeeded — session cookie is set
                router.push(callbackUrl);
                // Re-enable the form if navigation fails (e.g. redirect back to /login).
                setIsLoading(null);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            if (message.toLowerCase().includes("timed out")) {
                setError("Der Server antwortet nicht. Bitte prüfen Sie Backend/DB und versuchen Sie es erneut.");
            } else {
                setError("Ein Fehler ist aufgetreten");
            }
            setIsLoading(null);
        }
    }

    const handleResendVerification = async () => {
        const email = emailValue.trim();
        if (!email) {
            setResendStatus("Bitte geben Sie zuerst Ihre E-Mail-Adresse ein.");
            return;
        }

        setResendLoading(true);
        setResendStatus(null);
        const result = await requestEmailVerificationByEmail(email);
        if (!result.success) {
            setResendStatus(result.error);
        } else {
            setResendStatus("Bestätigungs-E-Mail wurde erneut gesendet.");
        }
        setResendLoading(false);
    };

    const handleSocialLogin = (provider: string) => {
        setIsLoading(provider);
        setLoginRedirectCookie();
        authClient.signIn.social({
            provider: provider as "google" | "facebook" | "apple",
            callbackURL: "/dashboard?fromLogin=1",
        });
    };

    const handlePasskeyLogin = async () => {
        setIsLoading("passkey");
        setError(null);
        try {
            const result = await authClient.signIn.passkey();
            if (result?.error) {
                setError("Passkey-Anmeldung fehlgeschlagen.");
                setIsLoading(null);
                return;
            }
            // Session cookie is set — redirect to dashboard
            setLoginRedirectCookie();
            router.push("/dashboard?fromLogin=1");
        } catch (err) {
            if (err instanceof Error && err.name === "NotAllowedError") {
                // User cancelled the passkey dialog — clear loading, no error
                setIsLoading(null);
            } else {
                console.error("[Passkey] Login failed:", err);
                setError("Passkey-Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.");
                setIsLoading(null);
            }
        }
    };

    return (
        // Solid card with subtle border - Enterprise SaaS style
        <div className="w-full bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800/60 rounded-2xl shadow-2xl shadow-black/40 pt-4 pb-10 px-6 sm:px-12 sm:pb-12 sm:pt-10">
            {/* Logo Section - Adjusted for better spacing on mobile */}
            <div className="flex flex-col items-center space-y-4 sm:space-y-6 mb-8 sm:mb-10">
                <div className="relative mt-0">
                    {/* Very subtle glow behind logo */}
                    <div className="absolute -inset-8 bg-blue-500/5 blur-3xl rounded-full pointer-events-none" />
                    <picture>
                        { }
                        <img
                            src="/login-logo.png"
                            alt="Zähler-Portal Logo"
                            className="relative w-[180px] h-auto sm:w-[280px]"
                        />
                    </picture>
                </div>
                <div className="text-center space-y-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-white tracking-[0.2em] uppercase">
                        Zähler-Portal
                    </h1>
                    <p className="hidden sm:block text-base text-slate-300 font-medium">
                        Anmeldung
                    </p>
                </div>
            </div>

            {/* Error Messages */}
            {error && (
                <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-400 text-center">
                    {error}
                </div>
            )}

            {/* Register hint after failed login */}
            {showRegisterHint && (
                <div className="mb-4 rounded-lg bg-amber-500/10 border border-amber-500/20 px-4 py-3 text-xs text-amber-300 space-y-1.5">
                    <p>
                        Falls Sie noch kein Konto haben, können Sie sich <Link href="/register" className="text-amber-200 underline underline-offset-2 hover:text-white font-medium">hier registrieren</Link>.
                    </p>
                    {orderedProviders.some(p => p.id === 'google') && (
                        <p>
                            <strong>Tipp:</strong> Oder ganz einfach mit &quot;Mit Google anmelden&quot; — kein Passwort nötig. Google übernimmt die sichere Authentifizierung für Sie.
                        </p>
                    )}
                </div>
            )}
            {verificationHint && (
                <div className="mb-4 rounded-lg bg-blue-500/10 border border-blue-500/20 px-4 py-3 text-xs text-blue-400 text-center">
                    {verificationHint}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        className="mt-3 h-9 w-full bg-transparent border-slate-600 hover:bg-slate-800 hover:border-slate-500 text-slate-300 text-xs rounded-lg transition-colors"
                    >
                        {resendLoading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Mail className="mr-2 h-3 w-3" />}
                        E-Mail erneut senden
                    </Button>
                </div>
            )}
            {resendStatus && (
                <div className="mb-4 rounded-lg bg-slate-700/30 border border-slate-600/30 px-4 py-3 text-xs text-slate-400 text-center">
                    {resendStatus}
                </div>
            )}


            {/* Login Form */}
            <form method="post" action="/login" onSubmit={onSubmit} className="space-y-3 sm:space-y-4">
                <div className="space-y-1.5">
                    <Label htmlFor="email" className="hidden sm:flex text-sm font-medium text-slate-200 items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        E-Mail-Adresse
                    </Label>
                    <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="E-Mail-Adresse"
                        required
                        disabled={!!isLoading}
                        value={emailValue}
                        onChange={(event) => setEmailValue(event.target.value)}
                        className="h-10 bg-[#0f172a] border-slate-700 text-slate-100 placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 rounded-md transition-all"
                    />
                </div>

                <div className="space-y-1.5">
                    <div className="hidden sm:flex items-center justify-between pr-0.5">
                        <Label htmlFor="password" className="text-sm font-medium text-slate-200 flex items-center gap-2">
                            <Lock className="w-4 h-4 text-slate-400" />
                            Passwort
                        </Label>
                        <Link
                            href={emailValue ? `/forgot-password?email=${encodeURIComponent(emailValue)}` : '/forgot-password'}
                            className="text-xs text-slate-400 hover:text-blue-400 transition-colors font-medium"
                        >
                            Passwort vergessen?
                        </Link>
                    </div>
                    <PasswordInput
                        id="password"
                        name="password"
                        placeholder="Passwort"
                        required
                        disabled={!!isLoading}
                        className="h-10 bg-[#0f172a] border-slate-700 text-slate-100 placeholder:text-slate-400 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/10 rounded-md transition-all"
                    />
                </div>

                {/* Primary Action */}
                <Button
                    type="submit"
                    className="h-11 w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-md transition-all duration-200 shadow-lg shadow-blue-500/30"
                    disabled={!!isLoading}
                >
                    {isLoading === "credentials" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Check className="mr-2 h-4 w-4" />
                    )}
                    Anmelden
                </Button>

                {/* Mobile only: Passwort vergessen + Register below button */}
                <div className="sm:hidden text-center mt-1">
                    <Link
                        href={emailValue ? `/forgot-password?email=${encodeURIComponent(emailValue)}` : '/forgot-password'}
                        className="text-xs text-slate-400 hover:text-blue-400 transition-colors font-medium"
                    >
                        Passwort vergessen?
                    </Link>
                </div>
                <Link href="/register" className="block">
                    <Button
                        type="button"
                        variant="outline"
                        className="h-10 w-full bg-transparent border-blue-500 hover:bg-blue-500/10 hover:border-blue-400 text-blue-400 hover:text-blue-300 font-semibold rounded-md transition-all duration-200"
                    >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Neues Konto erstellen
                    </Button>
                </Link>
            </form>

            {/* Social Login */}
            {orderedProviders.length > 0 && (
                <div className="mt-8 sm:mt-10 space-y-4">
                    <div className="flex items-center gap-4">
                        <Separator className="flex-1 bg-slate-600" />
                        <span className="text-xs text-slate-400 uppercase tracking-wider">oder</span>
                        <Separator className="flex-1 bg-slate-600" />
                    </div>
                    <div className={orderedProviders.length === 1 ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-2 gap-3'}>
                        {orderedProviders.map((provider) => (
                            <Button
                                key={provider.id}
                                variant="outline"
                                onClick={() => handleSocialLogin(provider.id)}
                                disabled={!!isLoading}
                                className="h-11 w-full bg-transparent border-slate-700 hover:bg-slate-800 hover:border-slate-600 text-slate-300 text-sm rounded-md flex items-center justify-center gap-2 transition-colors"
                            >
                                <ProviderIcon id={provider.id} />
                                {providerLabels[provider.id] || provider.name}
                            </Button>
                        ))}
                    </div>
                    {orderedProviders.some(p => p.id === 'google') && (
                        <p className="text-[10px] sm:text-[11px] text-slate-500 text-center">
                            Einfach mit Google — kein Passwort nötig.
                        </p>
                    )}
                </div>
            )}

            {/* Passkey Login */}
            {mounted && passkeySupported && hasPasskeys && (
                <div className="mt-4 space-y-4">
                    {orderedProviders.length === 0 && (
                        <div className="flex items-center gap-4">
                            <Separator className="flex-1 bg-slate-600" />
                            <span className="text-xs text-slate-400 uppercase tracking-wider">oder</span>
                            <Separator className="flex-1 bg-slate-600" />
                        </div>
                    )}
                    <Button
                        variant="outline"
                        onClick={handlePasskeyLogin}
                        disabled={!!isLoading}
                        className="h-11 w-full bg-transparent border-slate-700 hover:bg-slate-800 hover:border-slate-600 text-slate-300 text-sm rounded-md flex items-center justify-center gap-2 transition-colors"
                    >
                        {isLoading === "passkey" ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Fingerprint className="h-4 w-4" />
                        )}
                        Mit Passkey anmelden
                    </Button>
                </div>
            )}


        </div>
    );
}
